import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {
  getIndexPresets,
  getSignalsToday,
  triggerSignalScan,
} from "../Service/data";

const formatPrice = (val) => {
  if (val == null || val === undefined) return "—";
  return Number(val).toFixed(2);
};

const formatPct = (val) => {
  if (val == null || val === undefined) return "—";
  return `${Number(val).toFixed(2)}%`;
};

const SignalsPage = () => {
  const theme = useTheme();

  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("signal_stocks");
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [orderBy, setOrderBy] = useState("signal_type");
  const [order, setOrder] = useState("asc");

  const loadPresets = useCallback(() => {
    getIndexPresets()
      .then((data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          setPresets(data);
          const hasSignalStocks = data.some((p) => p.key === "signal_stocks");
          if (!hasSignalStocks && data.length > 0) {
            setSelectedPreset(data[0].key);
          }
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const loadSignals = useCallback(() => {
    setLoading(true);
    setError(null);
    getSignalsToday()
      .then((data) => {
        if (data && data.error) {
          setError(data.error);
          setSignals([]);
        } else if (Array.isArray(data)) {
          setSignals(data);
          setError(null);
        } else if (data && data.signals) {
          setSignals(data.signals);
          setError(null);
        } else {
          setSignals([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load signals. Is the backend running?");
        setSignals([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  const handleRunScan = () => {
    setScanning(true);
    setError(null);
    setScanResult(null);
    triggerSignalScan(selectedPreset)
      .then((data) => {
        if (data && data.error) {
          setError(data.error);
        } else {
          const msg = data.message || `Scan complete: ${data.count || 0} signals found from ${data.symbols_scanned || '?'} symbols`;
          setScanResult({
            message: msg,
            count: data.count || 0,
            symbols_scanned: data.symbols_scanned || 0,
            elapsed: data.elapsed_seconds || 0,
          });
          loadSignals();
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to run scan.");
      })
      .finally(() => setScanning(false));
  };

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const sortedSignals = React.useMemo(() => {
    const arr = [...signals];
    arr.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (orderBy === "signal_type" || orderBy === "symbol" || orderBy === "direction") {
        aVal = String(aVal ?? "").toLowerCase();
        bVal = String(bVal ?? "").toLowerCase();
      }
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [signals, orderBy, order]);

  const presetOptions = presets.length > 0
    ? presets
    : [
        { key: "nifty_50", label: "Nifty 50" },
        { key: "nifty_500", label: "Nifty 500" },
        { key: "nse_all", label: "NSE All" },
        { key: "crypto_commodities", label: "Crypto & Commodities" },
        { key: "signal_stocks", label: "Signal Stocks" },
      ];

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "100vw",
        minHeight: "100vh",
        background: theme.palette.background.default,
        p: 3,
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <NotificationsActiveIcon /> Live Signals
      </Typography>

      {/* Manual Scan Section */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Manual Scan
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {scanResult && !error && (
          <Alert
            severity={scanResult.count > 0 ? "success" : "info"}
            sx={{ mb: 2 }}
            onClose={() => setScanResult(null)}
          >
            {scanResult.message}
            {scanResult.count === 0 && " — No stocks met signal conditions on the latest trading day."}
          </Alert>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="preset-select-label">Preset</InputLabel>
              <Select
                labelId="preset-select-label"
                value={selectedPreset}
                label="Preset"
                onChange={(e) => setSelectedPreset(e.target.value)}
                disabled={scanning}
              >
                {presetOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRunScan}
              disabled={scanning || loading}
              fullWidth
            >
              {scanning ? "Scanning..." : "Run Scan"}
            </Button>
          </Grid>
          {scanning && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Scanning {selectedPreset} symbols... This may take a few seconds.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Signals Table */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Today&apos;s Signals ({signals.length})
        </Typography>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "symbol"}
                    direction={orderBy === "symbol" ? order : "asc"}
                    onClick={() => handleSort("symbol")}
                  >
                    Symbol
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "signal_type"}
                    direction={orderBy === "signal_type" ? order : "asc"}
                    onClick={() => handleSort("signal_type")}
                  >
                    Signal Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "direction"}
                    direction={orderBy === "direction" ? order : "asc"}
                    onClick={() => handleSort("direction")}
                  >
                    Direction
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Entry Price</TableCell>
                <TableCell align="right">Stop Loss</TableCell>
                <TableCell align="right">Take Profit</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Loading signals...</Typography>
                  </TableCell>
                </TableRow>
              ) : sortedSignals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No signals for today.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedSignals.map((row, idx) => {
                  const dirUpper = String(row.direction ?? "").toUpperCase();
                  const isBuy = dirUpper === "BUY" || dirUpper === "LONG";
                  const rowBg = isBuy
                    ? "rgba(76, 175, 80, 0.08)"
                    : "rgba(244, 67, 54, 0.08)";
                  return (
                    <TableRow
                      key={row.id ?? idx}
                      sx={{
                        backgroundColor: rowBg,
                        "&:hover": {
                          backgroundColor: isBuy
                            ? "rgba(76, 175, 80, 0.12)"
                            : "rgba(244, 67, 54, 0.12)",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.symbol ?? "—"}</TableCell>
                      <TableCell>{row.signal_type ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.direction ?? "—"}
                          size="small"
                          color={isBuy ? "success" : "error"}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="right">{formatPrice(row.entry_price)}</TableCell>
                      <TableCell align="right">
                        {formatPrice(row.stop_loss)}
                        {row.stop_loss_pct != null && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({formatPct(row.stop_loss_pct)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatPrice(row.take_profit)}
                        {row.take_profit_pct != null && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({formatPct(row.take_profit_pct)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.date ?? row.signal_date ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default SignalsPage;
